import { defineMessages } from '@sm/intl';
import PROJECT_NAME from '~app/pages/Pricing/constants/project';

export const TYPE_HEADER = '[Type: Header][Vis: high] - Feature Set List Item Tooltip Headings';
export const TYPE_PARAGRAPH = '[Type: Paragraph][Vis: high] - Feature Set List Item Tooltip';
const NUM_RESPONSES = 'Number of responses';
const NUM_RESPONSES_TOOLTIP =
  'Each plan has a limited number of responses as indicated. Basic users must purchase a paid plan to view responses in excess of the Response Limit. For other plans, users will be invoiced up to {overageCost} or equivalent, for each response over the indicated Response Limit.';
const SUPPORT = '24/7 Customer support via email';
const SUPPORT_TOOLTIP =
  'We offer 24/7 email support for all customers. Emails from ADVANTAGE and PREMIER customers are prioritized to receive responses as quickly as possible. We also offer phone support for our PREMIER customers.';
const CONTRIBUTOR_SEATS = 'Contributor seats';
const CONTRIBUTOR_SEATS_TOOLTIP =
  'While purchasing a team plan, you can add Contributor seats as a cost-effective way to include more stakeholders to analyze survey results. Contributors can use SurveyMonkey analysis features but cannot create or send surveys. Full Users on your team can share surveys with Contributors.';
const SENTIMENT_ANALYSIS = 'Sentiment analysis';
const SENTIMENT_ANALYSIS_TOOLTIP = `We'll automatically label your text responses as positive, neutral, or negative to reveal the emotion behind what people are saying.`;
const DATA_EXPORTS = 'Data exports';
const DATA_EXPORTS_TOOLTIP = `Create and print custom charts and reports quickly and easily. You'll be able to download survey results in several file formats, including as CSV, PDF, PPT, or XLS files. Export for SPSS for Advantage and Premier members.`;
const ADVANCED_LOGIC = 'Advanced survey logic';
const ADVANCED_LOGIC_TOOLTIP =
  'Enhance your survey data with access to advanced branching, advanced piping, block randomization, quotas, and more. Use custom variables to track data about respondents by passing one or more values through a survey link and into your survey results.';
const ADVANCED_SURVEY_LOGIC = 'Advanced survey analysis';
const ADVANCED_SURVEY_LOGIC_TOOLTIP =
  'Get more context for your data. See if there are statistically significant differences between response groups. And use text analysis to search and categorize open-ended responses and quickly see frequently used words and phrases.';

export default defineMessages({
  a_b_testing_tooltip_heading: {
    id: 'Pricing.tooltipCopy.ABTestingTooltipHeading',
    defaultMessage: 'A/B test question type',
    desc: {
      id: 'Pricing.tooltipCopy.ABTestingTooltipHeading',
      message: `${TYPE_HEADER} - ABTestingTooltipHeading`,
      project: PROJECT_NAME,
    },
  },
  a_b_testing_tooltip: {
    id: 'Pricing.tooltipCopy.ABTestingTooltip',
    defaultMessage:
      'Conduct A/B tests and test for answer bias by randomly segmenting respondents to see different images or text.',
    desc: {
      id: 'Pricing.tooltipCopy.ABTestingTooltip',
      message: `${TYPE_PARAGRAPH} - 1`,
      project: PROJECT_NAME,
    },
  },
  account_control_tooltip_heading: {
    id: 'Pricing.tooltipCopy.AccountControlTooltipHeading',
    defaultMessage: 'Account control',
    desc: {
      id: 'Pricing.tooltipCopy.AccountControlTooltipHeading',
      message: `${TYPE_HEADER} - 2`,
      project: PROJECT_NAME,
    },
  },
  account_control_tooltip: {
    id: 'Pricing.tooltipCopy.AccountControlTooltip',
    defaultMessage: `Automate onboarding users. Seamlessly enable new users to sign up or migrate existing users into your organization's SurveyMonkey Enterprise account.`,
    desc: {
      id: 'Pricing.tooltipCopy.AccountControlTooltip',
      message: `${TYPE_PARAGRAPH} - 3`,
      project: PROJECT_NAME,
    },
  },
  add_users_tooltip_heading: {
    id: 'Pricing.tooltipCopy.AddUsersTooltipHeading',
    defaultMessage: 'Additional Full User accounts',
    desc: {
      id: 'Pricing.tooltipCopy.AddUsersTooltipHeading',
      message: `${TYPE_HEADER} - 4`,
      project: PROJECT_NAME,
    },
  },
  add_users_tooltip: {
    id: 'Pricing.tooltipCopy.AddUsersTooltip',
    defaultMessage: `Business plans start at 3 Full Users, and allow you to invite more Full Users to your SurveyMonkey team for an additional fee. While purchasing a plan with a minimum of 3 users, you can add Contributor seats in bundles of 2 as a cost-effective way to add stakeholders to focus solely on analyzing survey results. <a href="/mp/collaboration/?ut_source=pricing-details" target="_blank" rel="noreferrer">Learn More »</a>`,
    desc: {
      id: 'Pricing.tooltipCopy.AddUsersTooltip',
      message: `${TYPE_PARAGRAPH} - 5`,
      project: PROJECT_NAME,
    },
  },
  admin_dashboard_tooltip_heading: {
    id: 'Pricing.tooltipCopy.AdminDashboardTooltipHeading',
    defaultMessage: 'Admin dashboard',
    desc: {
      id: 'Pricing.tooltipCopy.AdminDashboardTooltipHeading',
      message: `${TYPE_HEADER} - 6`,
      project: PROJECT_NAME,
    },
  },
  admin_dashboard_tooltip: {
    id: 'Pricing.tooltipCopy.AdminDashboardTooltip',
    defaultMessage: `Get visibility into your team's activity. View usage metrics like the number of surveys each user has in their account, and the date of their last login. You can also preview surveys in each account too.`,
    desc: {
      id: 'Pricing.tooltipCopy.AdminDashboardTooltip',
      message: `${TYPE_PARAGRAPH} - 7`,
      project: PROJECT_NAME,
    },
  },
  advanced_analyze_features_tooltip_heading: {
    id: 'Pricing.tooltipCopy.AdvancedAnalyzeFeaturesTooltipHeading',
    defaultMessage: ADVANCED_SURVEY_LOGIC,
    desc: {
      id: 'Pricing.tooltipCopy.AdvancedAnalyzeFeaturesTooltipHeading',
      message: `${TYPE_HEADER} - 8`,
      project: PROJECT_NAME,
    },
  },
  advanced_analyze_features_tooltip: {
    id: 'Pricing.tooltipCopy.AdvancedAnalyzeFeaturesTooltip',
    defaultMessage: ADVANCED_SURVEY_LOGIC_TOOLTIP,
    desc: {
      id: 'Pricing.tooltipCopy.AdvancedAnalyzeFeaturesTooltip',
      message: `${TYPE_PARAGRAPH} - 9`,
      project: PROJECT_NAME,
    },
  },
  advanced_collaboration_tooltip_heading: {
    id: 'Pricing.tooltipCopy.AdvancedCollaborationTooltipHeading',
    defaultMessage: CONTRIBUTOR_SEATS,
    desc: {
      id: 'Pricing.tooltipCopy.AdvancedCollaborationTooltipHeading',
      message: `${TYPE_HEADER} - 10`,
      project: PROJECT_NAME,
    },
  },
  advanced_collaboration_tooltip: {
    id: 'Pricing.tooltipCopy.AdvancedCollaborationTooltip',
    defaultMessage: CONTRIBUTOR_SEATS_TOOLTIP,
    desc: {
      id: 'Pricing.tooltipCopy.AdvancedCollaborationTooltip',
      message: `${TYPE_PARAGRAPH} - 11`,
      project: PROJECT_NAME,
    },
  },
  advanced_logic_tooltip_heading: {
    id: 'Pricing.tooltipCopy.AdvancedLogicTooltipHeading',
    defaultMessage: ADVANCED_LOGIC,
    desc: {
      id: 'Pricing.tooltipCopy.AdvancedLogicTooltipHeading',
      message: `${TYPE_HEADER} - 12`,
      project: PROJECT_NAME,
    },
  },
  advanced_logic_tooltip: {
    id: 'Pricing.tooltipCopy.AdvancedLogicTooltip',
    defaultMessage: ADVANCED_LOGIC_TOOLTIP,
    desc: {
      id: 'Pricing.tooltipCopy.AdvancedLogicTooltip',
      message: `${TYPE_PARAGRAPH} - 13`,
      project: PROJECT_NAME,
    },
  },
  all_data_exports_tooltip_heading: {
    id: 'Pricing.tooltipCopy.AllDataExportsTooltipHeading',
    defaultMessage: DATA_EXPORTS,
    desc: {
      id: 'Pricing.tooltipCopy.AllDataExportsTooltipHeading',
      message: `${TYPE_HEADER} - 14`,
      project: PROJECT_NAME,
    },
  },
  all_data_exports_tooltip: {
    id: 'Pricing.tooltipCopy.AllDataExportsTooltip',
    defaultMessage: DATA_EXPORTS_TOOLTIP,
    desc: {
      id: 'Pricing.tooltipCopy.AllDataExportsTooltip',
      message: `${TYPE_PARAGRAPH} - 15`,
      project: PROJECT_NAME,
    },
  },
  all_languages_supported_tooltip_heading: {
    id: 'Pricing.tooltipCopy.AllLanguagesSupportedTooltipHeading',
    defaultMessage: 'All languages supported',
    desc: {
      id: 'Pricing.tooltipCopy.AllLanguagesSupportedTooltipHeading',
      message: `${TYPE_HEADER} - 16`,
      project: PROJECT_NAME,
    },
  },
  all_languages_supported_tooltip: {
    id: 'Pricing.tooltipCopy.AllLanguagesSupportedTooltip',
    defaultMessage:
      'Create surveys in any language. You can write in unicode or multi-byte languages, and choose navigation buttons, help text, and error messages from 58 languages.',
    desc: {
      id: 'Pricing.tooltipCopy.AllLanguagesSupportedTooltip',
      message: `${TYPE_PARAGRAPH} - 17`,
      project: PROJECT_NAME,
    },
  },
  analyze_can_share_customize_branding_tooltip_heading: {
    id: 'Pricing.tooltipCopy.AnalyzeCanShareCustomizeBrandingTooltipHeading',
    defaultMessage: 'Customization and branding',
    desc: {
      id: 'Pricing.tooltipCopy.AnalyzeCanShareCustomizeBrandingTooltipHeading',
      message: `${TYPE_HEADER} - 18`,
      project: PROJECT_NAME,
    },
  },
  analyze_can_share_customize_branding_tooltip: {
    id: 'Pricing.tooltipCopy.AnalyzeCanShareCustomizeBrandingTooltip',
    defaultMessage:
      'Ensure a consistent brand experience for all survey-takers and enjoy customization features like Custom Subdomain, Custom Terms of Use and Custom Question Bank.',
    desc: {
      id: 'Pricing.tooltipCopy.AnalyzeCanShareCustomizeBrandingTooltip',
      message: `${TYPE_PARAGRAPH} - 19`,
      project: PROJECT_NAME,
    },
  },
  analyze_can_ta_tag_tooltip_heading: {
    id: 'Pricing.tooltipCopy.AnalyzeCanTATagTooltipHeading',
    defaultMessage: 'Tag written answers',
    desc: {
      id: 'Pricing.tooltipCopy.AnalyzeCanTATagTooltipHeading',
      message: `${TYPE_HEADER} - 20`,
      project: PROJECT_NAME,
    },
  },
  analyze_can_ta_tag_tooltip: {
    id: 'Pricing.tooltipCopy.AnalyzeCanTATagTooltip',
    defaultMessage:
      'Tag written answers to organize them by topic and uncover trends. See the percentage of answers that include each tag.',
    desc: {
      id: 'Pricing.tooltipCopy.AnalyzeCanTATagTooltip',
      message: `${TYPE_PARAGRAPH} - 21`,
      project: PROJECT_NAME,
    },
  },
  analyze_combine_filters_tooltip_heading: {
    id: 'Pricing.tooltipCopy.CombineFiltersTooltipHeading',
    defaultMessage: 'Filter and compare rules',
    desc: {
      id: 'Pricing.tooltipCopy.CombineFiltersTooltipHeading',
      message: `${TYPE_HEADER} - 22`,
      project: PROJECT_NAME,
    },
  },
  analyze_combine_filters_tooltip: {
    id: 'Pricing.tooltipCopy.CombineFiltersTooltip',
    defaultMessage:
      'Use filter and compare rules to segment your data and discover hidden trends. With a Basic plan, you can apply and save one rule to your data, but paid plans allow you to save and apply an unlimited number of rules. Data trends let you see how your results have changed over time.',
    desc: {
      id: 'Pricing.tooltipCopy.CombineFiltersTooltip',
      message: `${TYPE_PARAGRAPH} - 23`,
      project: PROJECT_NAME,
    },
  },
  analyze_dashboard_password_enabled_tooltip_heading: {
    id: 'Pricing.tooltipCopy.AnalyzeDashboardPasswordEnabledTooltipHeading',
    defaultMessage: 'Dashboard: Password protection',
    desc: {
      id: 'Pricing.tooltipCopy.AnalyzeDashboardPasswordEnabledTooltipHeading',
      message: `${TYPE_HEADER} - 24`,
      project: PROJECT_NAME,
    },
  },
  analyze_dashboard_password_enabled_tooltip: {
    id: 'Pricing.tooltipCopy.AnalyzeDashboardPasswordEnabledTooltip',
    defaultMessage: 'Set a password for your dashboards for share more securely.',
    desc: {
      id: 'Pricing.tooltipCopy.AnalyzeDashboardPasswordEnabledTooltip',
      message: `${TYPE_PARAGRAPH} - 25`,
      project: PROJECT_NAME,
    },
  },
  analyze_export_enabled_tooltip_heading: {
    id: 'Pricing.tooltipCopy.AnalyzeExportEnabledTooltipHeading',
    defaultMessage: DATA_EXPORTS,
    desc: {
      id: 'Pricing.tooltipCopy.AnalyzeExportEnabledTooltipHeading',
      message: `${TYPE_HEADER} - 26`,
      project: PROJECT_NAME,
    },
  },
  analyze_export_enabled_tooltip: {
    id: 'Pricing.tooltipCopy.AnalyzeExportEnabledTooltip',
    defaultMessage: DATA_EXPORTS_TOOLTIP,
    desc: {
      id: 'Pricing.tooltipCopy.AnalyzeExportEnabledTooltip',
      message: `${TYPE_PARAGRAPH} - 27`,
      project: PROJECT_NAME,
    },
  },
  analyze_export_spss_enabled_tooltip_heading: {
    id: 'Pricing.tooltipCopy.AnalyzeExportSpssEnabledTooltipHeading',
    defaultMessage: 'Advanced data exports',
    desc: {
      id: 'Pricing.tooltipCopy.AnalyzeExportSpssEnabledTooltipHeading',
      message: `${TYPE_HEADER} - 28`,
      project: PROJECT_NAME,
    },
  },
  analyze_export_spss_enabled_tooltip: {
    id: 'Pricing.tooltipCopy.AnalyzeExportSpssEnabledTooltip',
    defaultMessage:
      'Download your survey results into SPSS—along with CSV, PDF, PPT, or XLS files too. Also create and print custom charts and reports quickly and easily.',
    desc: {
      id: 'Pricing.tooltipCopy.AnalyzeExportSpssEnabledTooltip',
      message: `${TYPE_PARAGRAPH} - 29`,
      project: PROJECT_NAME,
    },
  },
  analyze_integrations_tooltip_heading: {
    id: 'Pricing.tooltipCopy.AnalyzeIntegrationsTooltipHeading',
    defaultMessage: 'Integrations',
    desc: {
      id: 'Pricing.tooltipCopy.AnalyzeIntegrationsTooltipHeading',
      message: `${TYPE_HEADER} - 30`,
      project: PROJECT_NAME,
    },
  },
  analyze_integrations_tooltip: {
    id: 'Pricing.tooltipCopy.AnalyzeIntegrationsTooltip',
    defaultMessage: `SurveyMonkey integrates with popular apps like Office 365, Google Drive, and Slack, so it's easy to use with your existing workflows.`,
    desc: {
      id: 'Pricing.tooltipCopy.AnalyzeIntegrationsTooltip',
      message: `${TYPE_PARAGRAPH} - 31`,
      project: PROJECT_NAME,
    },
  },
  analyze_results_together_tooltip_heading: {
    id: 'Pricing.tooltipCopy.AnalyzeResultsTogetherTooltipHeading',
    defaultMessage: 'Analyze results together',
    desc: {
      id: 'Pricing.tooltipCopy.AnalyzeResultsTogetherTooltipHeading',
      message: `${TYPE_HEADER} - 32`,
      project: PROJECT_NAME,
    },
  },
  analyze_results_together_tooltip: {
    id: 'Pricing.tooltipCopy.AnalyzeResultsTogetherTooltip',
    defaultMessage: 'Give your team access to survey results and export tools.',
    desc: {
      id: 'Pricing.tooltipCopy.AnalyzeResultsTogetherTooltip',
      message: `${TYPE_PARAGRAPH} - 33`,
      project: PROJECT_NAME,
    },
  },
  analyze_sentiment_enabled_tooltip_heading: {
    id: 'Pricing.tooltipCopy.AnalyzeSentimentEnabledTooltipHeading',
    defaultMessage: SENTIMENT_ANALYSIS,
    desc: {
      id: 'Pricing.tooltipCopy.AnalyzeSentimentEnabledTooltipHeading',
      message: `${TYPE_HEADER} - 34`,
      project: PROJECT_NAME,
    },
  },
  analyze_sentiment_enabled_tooltip: {
    id: 'Pricing.tooltipCopy.AnalyzeSentimentEnabledTooltip',
    defaultMessage: SENTIMENT_ANALYSIS_TOOLTIP,
    desc: {
      id: 'Pricing.tooltipCopy.AnalyzeSentimentEnabledTooltip',
      message: `${TYPE_PARAGRAPH} - 35`,
      project: PROJECT_NAME,
    },
  },
  sentiment_tooltip_heading: {
    id: 'Pricing.tooltipCopy.SentimentAnalysisEnabledTooltipHeading',
    defaultMessage: SENTIMENT_ANALYSIS,
    desc: {
      id: 'Pricing.tooltipCopy.SentimentAnalysisEnabledTooltipHeading',
      message: `${TYPE_HEADER} - 36`,
      project: PROJECT_NAME,
    },
  },
  sentiment_tooltip: {
    id: 'Pricing.tooltipCopy.SentimentAnalysisEnabledTooltip',
    defaultMessage: SENTIMENT_ANALYSIS_TOOLTIP,
    desc: {
      id: 'Pricing.tooltipCopy.SentimentAnalysisEnabledTooltip',
      message: `${TYPE_PARAGRAPH} - 37`,
      project: PROJECT_NAME,
    },
  },
  analyze_ta_enabled_tooltip_heading: {
    id: 'Pricing.tooltipCopy.AnalyzeTAEnabledTooltipHeading',
    defaultMessage: 'Word clouds',
    desc: {
      id: 'Pricing.tooltipCopy.AnalyzeTAEnabledTooltipHeading',
      message: `${TYPE_HEADER} - 38`,
      project: PROJECT_NAME,
    },
  },
  analyze_ta_enabled_tooltip: {
    id: 'Pricing.tooltipCopy.AnalyzeTAEnabledTooltip',
    defaultMessage:
      'Quickly spot trends and common themes in written answers. Words with lots of mentions appear bigger so you can get a quick understanding of what matters most to your respondents.',
    desc: {
      id: 'Pricing.tooltipCopy.AnalyzeTAEnabledTooltip',
      message: `${TYPE_PARAGRAPH} - 39`,
      project: PROJECT_NAME,
    },
  },
  analyze_trends_enabled_tooltip_heading: {
    id: 'Pricing.tooltipCopy.AnalyzeTrendsEnabledTooltipHeading',
    defaultMessage: 'Data trends',
    desc: {
      id: 'Pricing.tooltipCopy.AnalyzeTrendsEnabledTooltipHeading',
      message: `${TYPE_HEADER} - 40`,
      project: PROJECT_NAME,
    },
  },
  analyze_trends_enabled_tooltip: {
    id: 'Pricing.tooltipCopy.AnalyzeTrendsEnabledTooltip',
    defaultMessage: 'View your results trended over time to spot changes when they happen.',
    desc: {
      id: 'Pricing.tooltipCopy.AnalyzeTrendsEnabledTooltip',
      message: `${TYPE_PARAGRAPH} - 41`,
      project: PROJECT_NAME,
    },
  },
  base_response_count_tooltip_heading: {
    id: 'Pricing.tooltipCopy.BaseResponseCountTooltipHeading',
    defaultMessage: NUM_RESPONSES,
    desc: {
      id: 'Pricing.tooltipCopy.BaseResponseCountTooltipHeading',
      message: `${TYPE_HEADER} - 42`,
      project: PROJECT_NAME,
    },
  },
  base_response_count_tooltip: {
    id: 'Pricing.tooltipCopy.BaseResponseCountTooltip',
    defaultMessage: NUM_RESPONSES_TOOLTIP,
    desc: {
      id: 'Pricing.tooltipCopy.BaseResponseCountTooltip',
      message: `${TYPE_PARAGRAPH} - 43`,
      project: PROJECT_NAME,
    },
  },
  benchmarks_tooltip_heading: {
    id: 'Pricing.tooltipCopy.BenchmarksTooltipHeading',
    defaultMessage: 'SurveyMonkey industry benchmarks',
    desc: {
      id: 'Pricing.tooltipCopy.BenchmarksTooltipHeading',
      message: `${TYPE_HEADER} - 44`,
      project: PROJECT_NAME,
    },
  },
  benchmarks_tooltip: {
    id: 'Pricing.tooltipCopy.BenchmarksTooltip',
    defaultMessage: `Compare your results against the Industry Benchmark to understand how you're doing in the context of your greater industry. See your results in context to see how you measure up against your peers.`,
    desc: {
      id: 'Pricing.tooltipCopy.BenchmarksTooltip',
      message: `${TYPE_PARAGRAPH} - 45`,
      project: PROJECT_NAME,
    },
  },
  block_randomization_tooltip_heading: {
    id: 'Pricing.tooltipCopy.BlockRandomizationTooltipHeading',
    defaultMessage: 'Block randomization',
    desc: {
      id: 'Pricing.tooltipCopy.BlockRandomizationTooltipHeading',
      message: `${TYPE_HEADER} - 46`,
      project: PROJECT_NAME,
    },
  },
  block_randomization_tooltip: {
    id: 'Pricing.tooltipCopy.BlockRandomizationTooltip',
    defaultMessage:
      'Blocks are groups of pages, where each group contains related questions about a particular concept you want to test. You can use block randomization logic to rotate, flip, or randomize blocks to minimize order bias.',
    desc: {
      id: 'Pricing.tooltipCopy.BlockRandomizationTooltip',
      message: `${TYPE_PARAGRAPH} - 47`,
      project: PROJECT_NAME,
    },
  },
  build_surveys_together_tooltip_heading: {
    id: 'Pricing.tooltipCopy.BuildSurveysTogetherTooltipHeading',
    defaultMessage: 'Build Surveys Together',
    desc: {
      id: 'Pricing.tooltipCopy.BuildSurveysTogetherTooltipHeading',
      message: `${TYPE_HEADER} - 48`,
      project: PROJECT_NAME,
    },
  },
  build_surveys_together_tooltip: {
    id: 'Pricing.tooltipCopy.BuildSurveysTogetherTooltip',
    defaultMessage: 'Let people on your team view, edit, or comment on your surveys.',
    desc: {
      id: 'Pricing.tooltipCopy.BuildSurveysTogetherTooltip',
      message: `${TYPE_PARAGRAPH} - 49`,
      project: PROJECT_NAME,
    },
  },
  carry_forward_tooltip_heading: {
    id: 'Pricing.tooltipCopy.CarryForwardTooltipHeading',
    defaultMessage: 'Carry forward responses',
    desc: {
      id: 'Pricing.tooltipCopy.CarryForwardTooltipHeading',
      message: `${TYPE_HEADER} - 50`,
      project: PROJECT_NAME,
    },
  },
  carry_forward_tooltip: {
    id: 'Pricing.tooltipCopy.CarryForwardTooltip',
    defaultMessage: `Use each respondent's previous answer choices in a related question that appears later in your survey. You can carry forward either a respondent's selected or unselected answer choices and choose to show all or some previously selected or unselected answers. Responses that are carried forward cannot be edited.`,
    desc: {
      id: 'Pricing.tooltipCopy.CarryForwardTooltip',
      message: `${TYPE_PARAGRAPH} - 51`,
      project: PROJECT_NAME,
    },
  },
  click_map_qt_tooltip_heading: {
    id: 'Pricing.tooltipCopy.ClickMapQTTooltipHeading',
    defaultMessage: 'Click map data visualization',
    desc: {
      id: 'Pricing.tooltipCopy.ClickMapQTTooltipHeading',
      message: `${TYPE_HEADER} - 52`,
      project: PROJECT_NAME,
    },
  },
  click_map_qt_tooltip: {
    id: 'Pricing.tooltipCopy.ClickMapQTTooltip',
    defaultMessage: `Feedback on an image by tracking where and how many respondents click on it. When analyzing and presenting results, you'll be able to see each click represented by a corresponding dot, so it's clear which areas stand out to your audience.`,
    desc: {
      id: 'Pricing.tooltipCopy.ClickMapQTTooltip',
      message: `${TYPE_PARAGRAPH} - 53`,
      project: PROJECT_NAME,
    },
  },
  collaboration_tooltip_heading: {
    id: 'Pricing.tooltipCopy.CollaborationTooltipHeading',
    defaultMessage: 'Advanced permissions',
    desc: {
      id: 'Pricing.tooltipCopy.CollaborationTooltipHeading',
      message: `${TYPE_HEADER} - 54`,
      project: PROJECT_NAME,
    },
  },
  collaboration_tooltip: {
    id: 'Pricing.tooltipCopy.CollaborationTooltip',
    defaultMessage: 'Collaborate with fine control over who can view and edit your survey projects.',
    desc: {
      id: 'Pricing.tooltipCopy.CollaborationTooltip',
      message: `${TYPE_PARAGRAPH} - 55`,
      project: PROJECT_NAME,
    },
  },
  collaborate_tooltip_heading: {
    id: 'Pricing.tooltipCopy.CollaborateTooltipHeading',
    defaultMessage: 'Build surveys together',
    desc: {
      id: 'Pricing.tooltipCopy.CollaborateTooltipHeading',
      message: `${TYPE_HEADER} - 56`,
      project: PROJECT_NAME,
    },
  },
  collaborate_tooltip: {
    id: 'Pricing.tooltipCopy.CollaborateTooltip',
    defaultMessage: 'Let people on your team view, edit, or comment on your surveys.',
    desc: {
      id: 'Pricing.tooltipCopy.CollaborateTooltip',
      message: `${TYPE_PARAGRAPH} - 57`,
      project: PROJECT_NAME,
    },
  },
  collector_completion_url_enabled_tooltip_heading: {
    id: 'Pricing.tooltipCopy.CollectorCompletionUrlEnabledTooltipHeading',
    defaultMessage: 'Survey completion redirect',
    desc: {
      id: 'Pricing.tooltipCopy.CollectorCompletionUrlEnabledTooltipHeading',
      message: `${TYPE_HEADER} - 58`,
      project: PROJECT_NAME,
    },
  },
  collector_completion_url_enabled_tooltip: {
    id: 'Pricing.tooltipCopy.CollectorCompletionUrlEnabledTooltip',
    defaultMessage:
      'For a customized experience, redirect survey respondents to your website once they complete your survey.',
    desc: {
      id: 'Pricing.tooltipCopy.CollectorCompletionUrlEnabledTooltip',
      message: `${TYPE_PARAGRAPH} - 59`,
      project: PROJECT_NAME,
    },
  },
  collector_create_limit_tooltip_heading: {
    id: 'Pricing.tooltipCopy.CollectorCreateLimitTooltipHeading',
    defaultMessage: 'Number of collectors',
    desc: {
      id: 'Pricing.tooltipCopy.CollectorCreateLimitTooltipHeading',
      message: `${TYPE_HEADER} - 60`,
      project: PROJECT_NAME,
    },
  },
  collector_create_limit_tooltip: {
    id: 'Pricing.tooltipCopy.CollectorCreateLimitTooltip',
    defaultMessage:
      'Collectors are ways you can send your survey to respondents. Our collector types include weblinks, email, embedding on your website, and more.',
    desc: {
      id: 'Pricing.tooltipCopy.CollectorCreateLimitTooltip',
      message: `${TYPE_PARAGRAPH} - 61`,
      project: PROJECT_NAME,
    },
  },
  collector_mobile_sdk_enabled_tooltip_heading: {
    id: 'Pricing.tooltipCopy.CollectorMobileSdkEnabledTooltipHeading',
    defaultMessage: 'Mobile SDK',
    desc: {
      id: 'Pricing.tooltipCopy.CollectorMobileSdkEnabledTooltipHeading',
      message: `${TYPE_HEADER} - 62`,
      project: PROJECT_NAME,
    },
  },
  collector_mobile_sdk_enabled_tooltip: {
    id: 'Pricing.tooltipCopy.CollectorMobileSdkEnabledTooltip',
    defaultMessage: 'Integrate surveys directly into your iOS and Android mobile app.',
    desc: {
      id: 'Pricing.tooltipCopy.CollectorMobileSdkEnabledTooltip',
      message: `${TYPE_PARAGRAPH} - 63`,
      project: PROJECT_NAME,
    },
  },
  collector_friendly_url_enabled_tooltip_heading: {
    id: 'Pricing.tooltipCopy.CollectorFriendlyUrlEnabledTooltipHeading',
    defaultMessage: 'Custom SurveyMonkey URL',
    desc: {
      id: 'Pricing.tooltipCopy.CollectorFriendlyUrlEnabledTooltipHeading',
      message: `${TYPE_HEADER} - 64`,
      project: PROJECT_NAME,
    },
  },
  collector_friendly_url_enabled_tooltip: {
    id: 'Pricing.tooltipCopy.CollectorFriendlyUrlEnabledTooltip',
    defaultMessage: 'Write customized survey URLs, for example: http://www.surveymonkey.com/s/your-survey',
    desc: {
      id: 'Pricing.tooltipCopy.CollectorFriendlyUrlEnabledTooltip',
      message: `${TYPE_PARAGRAPH} - 65`,
      project: PROJECT_NAME,
    },
  },
  collector_white_label_enabled_tooltip_heading: {
    id: 'Pricing.tooltipCopy.CollectorWhiteLabelEnabledTooltipHeading',
    defaultMessage: 'White label surveys',
    desc: {
      id: 'Pricing.tooltipCopy.CollectorWhiteLabelEnabledTooltipHeading',
      message: `${TYPE_HEADER} - 66`,
      project: PROJECT_NAME,
    },
  },
  collector_white_label_enabled_tooltip: {
    id: 'Pricing.tooltipCopy.CollectorWhiteLabelEnabledTooltip',
    defaultMessage:
      'Completely customize and brand your surveys. Remove the SurveyMonkey footer from your surveys, change the domain of your survey link to research.net, and redirect survey respondents to your site upon survey completion.',
    desc: {
      id: 'Pricing.tooltipCopy.CollectorWhiteLabelEnabledTooltip',
      message: `${TYPE_PARAGRAPH} - 67`,
      project: PROJECT_NAME,
    },
  },
  consolidated_billing_tooltip_heading: {
    id: 'Pricing.tooltipCopy.ConsolidatedBillingTooltipHeading',
    defaultMessage: 'Consolidated billing',
    desc: {
      id: 'Pricing.tooltipCopy.ConsolidatedBillingTooltipHeading',
      message: `${TYPE_HEADER} - 68`,
      project: PROJECT_NAME,
    },
  },
  consolidated_billing_tooltip: {
    id: 'Pricing.tooltipCopy.ConsolidatedBillingTooltip',
    defaultMessage: 'Receive a consolidated bill for your entire team in SurveyMonkey.',
    desc: {
      id: 'Pricing.tooltipCopy.ConsolidatedBillingTooltip',
      message: `${TYPE_PARAGRAPH} - 69`,
      project: PROJECT_NAME,
    },
  },
  contributor_seats_tooltip_heading: {
    id: 'Pricing.tooltipCopy.ContributorSeatsTooltipHeading',
    defaultMessage: CONTRIBUTOR_SEATS,
    desc: {
      id: 'Pricing.tooltipCopy.ContributorSeatsTooltipHeading',
      message: `${TYPE_HEADER} - 70`,
      project: PROJECT_NAME,
    },
  },
  contributor_seats_tooltip: {
    id: 'Pricing.tooltipCopy.ContributorSeatsTooltip',
    defaultMessage: CONTRIBUTOR_SEATS_TOOLTIP,
    desc: {
      id: 'Pricing.tooltipCopy.ContributorSeatsTooltip',
      message: `${TYPE_PARAGRAPH} - 71`,
      project: PROJECT_NAME,
    },
  },
  create_advanced_features_tooltip_heading: {
    id: 'Pricing.tooltipCopy.CreateAdvancedFeaturesTooltipHeading',
    defaultMessage: 'A/B test question type',
    desc: {
      id: 'Pricing.tooltipCopy.CreateAdvancedFeaturesTooltipHeading',
      message: `${TYPE_HEADER} - 72`,
      project: PROJECT_NAME,
    },
  },
  create_advanced_features_tooltip: {
    id: 'Pricing.tooltipCopy.CreateAdvancedFeaturesTooltip',
    defaultMessage:
      'Conduct A/B tests and test for answer bias by randomly segmenting respondents to see different images or text.',
    desc: {
      id: 'Pricing.tooltipCopy.CreateAdvancedFeaturesTooltip',
      message: `${TYPE_PARAGRAPH} - 73`,
      project: PROJECT_NAME,
    },
  },
  create_custom_html_email_message_enabled_tooltip_heading: {
    id: 'Pricing.tooltipCopy.CreateCustomHtmlEmailMessageEnabledTooltipHeading',
    defaultMessage: 'Custom HTML email invitations',
    desc: {
      id: 'Pricing.tooltipCopy.CreateCustomHtmlEmailMessageEnabledTooltipHeading',
      message: `${TYPE_HEADER} - 74`,
      project: PROJECT_NAME,
    },
  },
  create_custom_html_email_message_enabled_tooltip: {
    id: 'Pricing.tooltipCopy.CreateCustomHtmlEmailMessageEnabledTooltip',
    defaultMessage:
      'Add custom designs, images, embed videos, or hyperlink text in your email messages used to send your survey.',
    desc: {
      id: 'Pricing.tooltipCopy.CreateCustomHtmlEmailMessageEnabledTooltip',
      message: `${TYPE_PARAGRAPH} - 75`,
      project: PROJECT_NAME,
    },
  },
  create_custom_theme_enabled_tooltip_heading: {
    id: 'Pricing.tooltipCopy.CreateCustomThemeEnabledTooltipHeading',
    defaultMessage: 'Custom themes, colors & more',
    desc: {
      id: 'Pricing.tooltipCopy.CreateCustomThemeEnabledTooltipHeading',
      message: `${TYPE_HEADER} - 76`,
      project: PROJECT_NAME,
    },
  },
  create_custom_theme_enabled_tooltip: {
    id: 'Pricing.tooltipCopy.CreateCustomThemeEnabledTooltip',
    defaultMessage:
      'Create custom themes to change the look and feel of your survey. You can edit the colors of different survey elements and choose from several web fonts for your survey.',
    desc: {
      id: 'Pricing.tooltipCopy.CreateCustomThemeEnabledTooltip',
      message: `${TYPE_PARAGRAPH} - 77`,
      project: PROJECT_NAME,
    },
  },
  create_custom_variables_enabled_tooltip_heading: {
    id: 'Pricing.tooltipCopy.CreateCustomVariablesEnabledTooltipHeading',
    defaultMessage: 'Custom variables',
    desc: {
      id: 'Pricing.tooltipCopy.CreateCustomVariablesEnabledTooltipHeading',
      message: `${TYPE_HEADER} - 78`,
      project: PROJECT_NAME,
    },
  },
  create_custom_variables_enabled_tooltip: {
    id: 'Pricing.tooltipCopy.CreateCustomVariablesEnabledTooltip',
    defaultMessage:
      'Use custom variables to track data about respondents by passing one or more values through a survey link and into your survey results.',
    desc: {
      id: 'Pricing.tooltipCopy.CreateCustomVariablesEnabledTooltip',
      message: `${TYPE_PARAGRAPH} - 79`,
      project: PROJECT_NAME,
    },
  },
  create_logo_enabled_tooltip_heading: {
    id: 'Pricing.tooltipCopy.CreateLogoEnabledTooltipHeading',
    defaultMessage: 'Customized surveys',
    desc: {
      id: 'Pricing.tooltipCopy.CreateLogoEnabledTooltipHeading',
      message: `${TYPE_HEADER} - 80`,
      project: PROJECT_NAME,
    },
  },
  create_logo_enabled_tooltip: {
    id: 'Pricing.tooltipCopy.CreateLogoEnabledTooltip',
    defaultMessage: `Customize your survey's look and feel by adding your logo, selecting colors and web fonts, and uploading images. Create and save custom themes to quickly build on-brand surveys. You can also modify the ending of your survey's URL. For example: www.surveymonkey.com/s/your-survey`,
    desc: {
      id: 'Pricing.tooltipCopy.CreateLogoEnabledTooltip',
      message: `${TYPE_PARAGRAPH} - 81`,
      project: PROJECT_NAME,
    },
  },
  create_piping_enabled_tooltip_heading: {
    id: 'Pricing.tooltipCopy.CreatePipingEnabledTooltipHeading',
    defaultMessage: 'Customized survey experience',
    desc: {
      id: 'Pricing.tooltipCopy.CreatePipingEnabledTooltipHeading',
      message: `${TYPE_HEADER} - 82`,
      project: PROJECT_NAME,
    },
  },
  create_piping_enabled_tooltip: {
    id: 'Pricing.tooltipCopy.CreatePipingEnabledTooltip',
    defaultMessage: `Get more ways to personalize each respondent's experience while taking your survey. Use skip logic to create custom paths through a survey based on each respondent's answers. With question & answer piping, you can insert answer text from a question into another question on a later page in the survey.`,
    desc: {
      id: 'Pricing.tooltipCopy.CreatePipingEnabledTooltip',
      message: `${TYPE_PARAGRAPH} - 83`,
      project: PROJECT_NAME,
    },
  },
  create_quotas_enabled_tooltip_heading: {
    id: 'Pricing.tooltipCopy.CreateQuotasEnabledTooltipHeading',
    defaultMessage: 'Response quotas',
    desc: {
      id: 'Pricing.tooltipCopy.CreateQuotasEnabledTooltipHeading',
      message: `${TYPE_HEADER} - 84`,
      project: PROJECT_NAME,
    },
  },
  create_quotas_enabled_tooltip: {
    id: 'Pricing.tooltipCopy.CreateQuotasEnabledTooltip',
    defaultMessage: 'Close your survey automatically when you get all the qualified responses you need.',
    desc: {
      id: 'Pricing.tooltipCopy.CreateQuotasEnabledTooltip',
      message: `${TYPE_PARAGRAPH} - 85`,
      project: PROJECT_NAME,
    },
  },
  create_randomization_enabled_tooltip_heading: {
    id: 'Pricing.tooltipCopy.CreateRandomizationEnabledTooltipHeading',
    defaultMessage: 'Question & page randomization',
    desc: {
      id: 'Pricing.tooltipCopy.CreateRandomizationEnabledTooltipHeading',
      message: `${TYPE_HEADER} - 86`,
      project: PROJECT_NAME,
    },
  },
  create_randomization_enabled_tooltip: {
    id: 'Pricing.tooltipCopy.CreateRandomizationEnabledTooltip',
    defaultMessage:
      'Reduce answer bias by randomizing the order of the pages in a survey or the order of questions within a page.',
    desc: {
      id: 'Pricing.tooltipCopy.CreateRandomizationEnabledTooltip',
      message: `${TYPE_PARAGRAPH} - 87`,
      project: PROJECT_NAME,
    },
  },
  create_skip_logic_enabled_tooltip_heading: {
    id: 'Pricing.tooltipCopy.CreateSkipLogicEnabledTooltipHeading',
    defaultMessage: 'Question and page skip logic',
    desc: {
      id: 'Pricing.tooltipCopy.CreateSkipLogicEnabledTooltipHeading',
      message: `${TYPE_HEADER} - 88`,
      project: PROJECT_NAME,
    },
  },
  create_skip_logic_enabled_tooltip: {
    id: 'Pricing.tooltipCopy.CreateSkipLogicEnabledTooltip',
    defaultMessage: `Create custom paths through surveys that change based on a respondent's answers. Skip logic is also known as 'conditional branching' or 'branch logic'.`,
    desc: {
      id: 'Pricing.tooltipCopy.CreateSkipLogicEnabledTooltip',
      message: `${TYPE_PARAGRAPH} - 89`,
      project: PROJECT_NAME,
    },
  },
  create_templates_enabled_tooltip_heading: {
    id: 'Pricing.tooltipCopy.CreateTemplatesEnabledTooltipHeading',
    defaultMessage: 'Expert, pre-written survey templates',
    desc: {
      id: 'Pricing.tooltipCopy.CreateTemplatesEnabledTooltipHeading',
      message: `${TYPE_HEADER} - 90`,
      project: PROJECT_NAME,
    },
  },
  create_templates_enabled_tooltip: {
    id: 'Pricing.tooltipCopy.CreateTemplatesEnabledTooltip',
    defaultMessage:
      'Choose from over 180 survey templates in our Question Bank library. All templates are certified by our expert methodologists.',
    desc: {
      id: 'Pricing.tooltipCopy.CreateTemplatesEnabledTooltip',
      message: `${TYPE_PARAGRAPH} - 91`,
      project: PROJECT_NAME,
    },
  },
  custom_question_bank_tooltip_heading: {
    id: 'Pricing.tooltipCopy.CustomQuestionBankTooltipHeading',
    defaultMessage: 'Custom Question Bank',
    desc: {
      id: 'Pricing.tooltipCopy.CustomQuestionBankTooltipHeading',
      message: `${TYPE_HEADER} - 92`,
      project: PROJECT_NAME,
    },
  },
  custom_question_bank_tooltip: {
    id: 'Pricing.tooltipCopy.CustomQuestionBankTooltip',
    defaultMessage:
      'Easily create pre-composed survey questions that are unique to your business, and to share them with your entire organization.',
    desc: {
      id: 'Pricing.tooltipCopy.CustomQuestionBankTooltip',
      message: `${TYPE_PARAGRAPH} - 93`,
      project: PROJECT_NAME,
    },
  },
  custom_subdomain_tooltip_heading: {
    id: 'Pricing.tooltipCopy.CustomSubdomainTooltipHeading',
    defaultMessage: 'Custom subdomain',
    desc: {
      id: 'Pricing.tooltipCopy.CustomSubdomainTooltipHeading',
      message: `${TYPE_HEADER} - 94`,
      project: PROJECT_NAME,
    },
  },
  custom_subdomain_tooltip: {
    id: 'Pricing.tooltipCopy.CustomSubdomainTooltip',
    defaultMessage:
      'Get a custom URL and landing page to ensure a seamlessly branded survey experience for your respondents.',
    desc: {
      id: 'Pricing.tooltipCopy.CustomSubdomainTooltip',
      message: `${TYPE_PARAGRAPH} - 95`,
      project: PROJECT_NAME,
    },
  },
  custom_templates_tooltip_heading: {
    id: 'Pricing.tooltipCopy.CustomTemplatesTooltipHeading',
    defaultMessage: 'Shared templates',
    desc: {
      id: 'Pricing.tooltipCopy.CustomTemplatesTooltipHeading',
      message: `${TYPE_HEADER} - 96`,
      project: PROJECT_NAME,
    },
  },
  custom_templates_tooltip: {
    id: 'Pricing.tooltipCopy.CustomTemplatesTooltip',
    defaultMessage: 'Turn your custom surveys into standard templates that everyone in your team can use.',
    desc: {
      id: 'Pricing.tooltipCopy.CustomTemplatesTooltip',
      message: `${TYPE_PARAGRAPH} - 97`,
      project: PROJECT_NAME,
    },
  },
  customer_success_tooltip_heading: {
    id: 'Pricing.tooltipCopy.CustomerSuccessTooltipHeading',
    defaultMessage: 'Customer success',
    desc: {
      id: 'Pricing.tooltipCopy.CustomerSuccessTooltipHeading',
      message: `${TYPE_HEADER} - 98`,
      project: PROJECT_NAME,
    },
  },
  customer_success_tooltip: {
    id: 'Pricing.tooltipCopy.CustomerSuccessTooltip',
    defaultMessage:
      'Get a dedicated customer success manager to help you with onboarding, training users as well as implementing best practices to ensure optimal use of the SurveyMonkey platform across your organization.',
    desc: {
      id: 'Pricing.tooltipCopy.CustomerSuccessTooltip',
      message: `${TYPE_HEADER} - 99`,
      project: PROJECT_NAME,
    },
  },
  custom_terms_of_use_tooltip_heading: {
    id: 'Pricing.tooltipCopy.CustomTermsOfUseTooltipHeading',
    defaultMessage: 'Custom Terms of Use',
    desc: {
      id: 'Pricing.tooltipCopy.CustomTermsOfUseTooltipHeading',
      message: `${TYPE_HEADER} - 100`,
      project: PROJECT_NAME,
    },
  },
  custom_terms_of_use_tooltip: {
    id: 'Pricing.tooltipCopy.CustomTermsOfUseTooltip',
    defaultMessage: `Ensure your employees opt in to your organization's Terms of Use for SurveyMonkey.`,
    desc: {
      id: 'Pricing.tooltipCopy.CustomTermsOfUseTooltip',
      message: `${TYPE_PARAGRAPH} - 101`,
      project: PROJECT_NAME,
    },
  },
  customer_success_manager_tooltip_heading: {
    id: 'Pricing.tooltipCopy.CustomerSuccessManagerTooltipHeading',
    defaultMessage: 'Customer Success Manager',
    desc: {
      id: 'Pricing.tooltipCopy.CustomerSuccessManagerTooltipHeading',
      message: `${TYPE_HEADER} - 102`,
      project: PROJECT_NAME,
    },
  },
  customer_success_manager_tooltip: {
    id: 'Pricing.tooltipCopy.CustomerSuccessManagerTooltip',
    defaultMessage:
      'Get a dedicated customer success manager to help you with onboarding, training users as well as implementing best practices to ensure optimal use of the SurveyMonkey platform across your organization.',
    desc: {
      id: 'Pricing.tooltipCopy.CustomerSuccessManagerTooltip',
      message: `${TYPE_PARAGRAPH} - 103`,
      project: PROJECT_NAME,
    },
  },
  dashboard_tooltip_heading: {
    id: 'Pricing.tooltipCopy.DashboardTooltipHeading',
    defaultMessage: 'Dashboard',
    desc: {
      id: 'Pricing.tooltipCopy.DashboardTooltipHeading',
      message: `${TYPE_HEADER} - 104`,
      project: PROJECT_NAME,
    },
  },
  dashboard_tooltip: {
    id: 'Pricing.tooltipCopy.DashboardTooltip',
    defaultMessage:
      'Tell stories with your data by arranging charts, images, and findings in a custom layout. Share your dashboard with anyone.',
    desc: {
      id: 'Pricing.tooltipCopy.DashboardTooltip',
      message: `${TYPE_PARAGRAPH} - 105`,
      project: PROJECT_NAME,
    },
  },
  email_support_tooltip_heading: {
    id: 'Pricing.tooltipCopy.EmailSupportTooltipHeading',
    defaultMessage: SUPPORT,
    desc: {
      id: 'Pricing.tooltipCopy.EmailSupportTooltipHeading',
      message: `${TYPE_HEADER} - 106`,
      project: PROJECT_NAME,
    },
  },
  email_support_tooltip: {
    id: 'Pricing.tooltipCopy.EmailSupportTooltip',
    defaultMessage: SUPPORT_TOOLTIP,
    desc: {
      id: 'Pricing.tooltipCopy.EmailSupportTooltip',
      message: `${TYPE_PARAGRAPH} - 107`,
      project: PROJECT_NAME,
    },
  },
  enhanced_governance_tooltip_heading: {
    id: 'Pricing.tooltipCopy.EnhancedGovernanceTooltipHeading',
    defaultMessage: 'Governance and security',
    desc: {
      id: 'Pricing.tooltipCopy.EnhancedGovernanceTooltipHeading',
      message: `${TYPE_HEADER} - 108`,
      project: PROJECT_NAME,
    },
  },
  enhanced_governance_tooltip: {
    id: 'Pricing.tooltipCopy.EnhancedGovernanceTooltip',
    defaultMessage:
      'Get access to a vast array of governance and security features, including SSO for user authentication, data encryption, access controls, SSAE-16 SOC II compliant data centers and more.',
    desc: {
      id: 'Pricing.tooltipCopy.EnhancedGovernanceTooltip',
      message: `${TYPE_PARAGRAPH} - 109`,
      project: PROJECT_NAME,
    },
  },
  essential_question_types_tooltip_heading: {
    id: 'Pricing.tooltipCopy.EssentialQuestionTypesTooltipHeading',
    defaultMessage: 'Essential question types',
    desc: {
      id: 'Pricing.tooltipCopy.EssentialQuestionTypesTooltipHeading',
      message: `${TYPE_HEADER} - 110`,
      project: PROJECT_NAME,
    },
  },
  essential_question_types_tooltip: {
    id: 'Pricing.tooltipCopy.EssentialQuestionTypesTooltip',
    defaultMessage: 'Use essential questions such as multiple choice, checkboxes, dropdown, and text box.',
    desc: {
      id: 'Pricing.tooltipCopy.EssentialQuestionTypesTooltip',
      message: `${TYPE_PARAGRAPH} - 111`,
      project: PROJECT_NAME,
    },
  },
  extended_piping_tooltip_heading: {
    id: 'Pricing.tooltipCopy.ExtendedPipingTooltipHeading',
    defaultMessage: ADVANCED_LOGIC,
    desc: {
      id: 'Pricing.tooltipCopy.ExtendedPipingTooltipHeading',
      message: `${TYPE_HEADER} - 112`,
      project: PROJECT_NAME,
    },
  },
  extended_piping_tooltip: {
    id: 'Pricing.tooltipCopy.ExtendedPipingTooltip',
    defaultMessage: ADVANCED_LOGIC_TOOLTIP,
    desc: {
      id: 'Pricing.tooltipCopy.ExtendedPipingTooltip',
      message: `${TYPE_PARAGRAPH} - 113`,
      project: PROJECT_NAME,
    },
  },
  extract_data_tooltip_heading: {
    id: 'Pricing.tooltipCopy.ExtractDataTooltipHeading',
    defaultMessage: 'Extract data',
    desc: {
      id: 'Pricing.tooltipCopy.ExtractDataTooltipHeading',
      message: `${TYPE_HEADER} - 114`,
      project: PROJECT_NAME,
    },
  },
  extract_data_tooltip: {
    id: 'Pricing.tooltipCopy.ExtractDataTooltip',
    defaultMessage: 'Extract and leverage your survey data using powerful 3rd-party integration tools.',
    desc: {
      id: 'Pricing.tooltipCopy.ExtractDataTooltip',
      message: `${TYPE_PARAGRAPH} - 115`,
      project: PROJECT_NAME,
    },
  },
  file_upload_tooltip_heading: {
    id: 'Pricing.tooltipCopy.FileUploadTooltipHeading',
    defaultMessage: 'File upload',
    desc: {
      id: 'Pricing.tooltipCopy.FileUploadTooltipHeading',
      message: `${TYPE_HEADER} - 116`,
      project: PROJECT_NAME,
    },
  },
  file_upload_tooltip: {
    id: 'Pricing.tooltipCopy.FileUploadTooltip',
    defaultMessage:
      'Allow respondents to upload a file as part of their survey response. You can add up to 20 file upload questions per survey.',
    desc: {
      id: 'Pricing.tooltipCopy.FileUploadTooltip',
      message: `${TYPE_PARAGRAPH} - 117`,
      project: PROJECT_NAME,
    },
  },
  flexible_plan_types_tooltip_heading: {
    id: 'Pricing.tooltipCopy.FlexiblePlanTypesTooltipHeading',
    defaultMessage: 'Flexible plan types',
    desc: {
      id: 'Pricing.tooltipCopy.FlexiblePlanTypesTooltipHeading',
      message: `${TYPE_HEADER} - 118`,
      project: PROJECT_NAME,
    },
  },
  flexible_plan_types_tooltip: {
    id: 'Pricing.tooltipCopy.FlexiblePlanTypesTooltip',
    defaultMessage: `Enjoy flexible and custom plan types to fit your organization's survey needs.`,
    desc: {
      id: 'Pricing.tooltipCopy.FlexiblePlanTypesTooltip',
      message: `${TYPE_PARAGRAPH} - 119`,
      project: PROJECT_NAME,
    },
  },
  footer_branding_tooltip_heading: {
    id: 'Pricing.tooltipCopy.AnalyzeCanExportHideBrandingTooltipHeading',
    defaultMessage: 'Remove footer',
    desc: {
      id: 'Pricing.tooltipCopy.AnalyzeCanExportHideBrandingTooltipHeading',
      message: `${TYPE_HEADER} - 120`,
      project: PROJECT_NAME,
    },
  },
  footer_branding_tooltip: {
    id: 'Pricing.tooltipCopy.AnalyzeCanExportHideBrandingTooltip',
    defaultMessage:
      'Turn off the "powered by SurveyMonkey" footer that appears by default in your survey design, email invitation messages, and embedded surveys.',
    desc: {
      id: 'Pricing.tooltipCopy.AnalyzeCanExportHideBrandingTooltip',
      message: `${TYPE_PARAGRAPH} - 121`,
      project: PROJECT_NAME,
    },
  },
  gather_comments_tooltip_heading: {
    id: 'Pricing.tooltipCopy.GatherCommentsTooltipHeading',
    defaultMessage: 'Commenting feature',
    desc: {
      id: 'Pricing.tooltipCopy.GatherCommentsTooltipHeading',
      message: `${TYPE_HEADER} - 122`,
      project: PROJECT_NAME,
    },
  },
  gather_comments_tooltip: {
    id: 'Pricing.tooltipCopy.GatherCommentsTooltip',
    defaultMessage:
      'Invite people to provide feedback on your survey projects all in one place. Our commenting feature allows you to share both survey drafts and results with anyone you want.',
    desc: {
      id: 'Pricing.tooltipCopy.GatherCommentsTooltip',
      message: `${TYPE_HEADER} - 123`,
      project: PROJECT_NAME,
    },
  },
  global_settings_tooltip_heading: {
    id: 'Pricing.tooltipCopy.GlobalSettingsTooltipHeading',
    defaultMessage: 'Global settings',
    desc: {
      id: 'Pricing.tooltipCopy.GlobalSettingsTooltipHeading',
      message: `${TYPE_HEADER} - 124`,
      project: PROJECT_NAME,
    },
  },
  global_settings_tooltip: {
    id: 'Pricing.tooltipCopy.GlobalSettingsTooltip',
    defaultMessage:
      'Create global settings for everyone in your plan. Includes PII management, collector and respondent data settings, and Audience credits management.',
    desc: {
      id: 'Pricing.tooltipCopy.GlobalSettingsTooltip',
      message: `${TYPE_PARAGRAPH} - 125`,
      project: PROJECT_NAME,
    },
  },
  hipaa_enabled_tooltip_heading: {
    id: 'Pricing.tooltipCopy.HipaaEnabledTooltipHeading',
    defaultMessage: 'HIPAA-compliant features',
    desc: {
      id: 'Pricing.tooltipCopy.HipaaEnabledTooltipHeading',
      message: `${TYPE_HEADER} - 126`,
      project: PROJECT_NAME,
    },
  },
  hipaa_enabled_tooltip: {
    id: 'Pricing.tooltipCopy.HipaaEnabledTooltip',
    defaultMessage:
      'Collect and manage Protected Health Information (PHI) through surveys. To enable HIPAA features, you must enter into a Business Associate Agreement (BAA) with SurveyMonkey.',
    desc: {
      id: 'Pricing.tooltipCopy.HipaaEnabledTooltip',
      message: `${TYPE_PARAGRAPH} - 127`,
      project: PROJECT_NAME,
    },
  },
  integrations_tooltip_heading: {
    id: 'Pricing.tooltipCopy.IntegrationsTooltipHeading',
    defaultMessage: 'Integrations',
    desc: {
      id: 'Pricing.tooltipCopy.IntegrationsTooltipHeading',
      message: `${TYPE_HEADER} - 128`,
      project: PROJECT_NAME,
    },
  },
  integrations_tooltip: {
    id: 'Pricing.tooltipCopy.IntegrationsTooltip',
    defaultMessage:
      'Integrate your survey data into important business applications that your organization already uses such as Salesforce, Marketo, Oracle Eloqua, and Tableau.',
    desc: {
      id: 'Pricing.tooltipCopy.IntegrationsTooltip',
      message: `${TYPE_PARAGRAPH} - 129`,
      project: PROJECT_NAME,
    },
  },
  enterprise_only_integrations_tooltip_heading: {
    id: 'Pricing.tooltipCopy.EnterpriseOnlyIntegrationsTooltipHeading',
    defaultMessage: 'Enterprise-only integrations',
    desc: {
      id: 'Pricing.tooltipCopy.EnterpriseOnlyIntegrationsTooltipHeading',
      message: `${TYPE_HEADER} - 130`,
      project: PROJECT_NAME,
    },
  },
  enterprise_only_integrations_tooltip: {
    id: 'Pricing.tooltipCopy.EnterpriseOnlyIntegrationsTooltip',
    defaultMessage:
      'Enterprise plans enjoy unlimited API access and can add on integrations with Salesforce, Marketo, Eloqua, Tableau, and more.',
    desc: {
      id: 'Pricing.tooltipCopy.EnterpriseOnlyIntegrationsTooltip',
      message: `${TYPE_PARAGRAPH} - 131`,
      project: PROJECT_NAME,
    },
  },
  kiosk_mode_inactivity_timer_tooltip_heading: {
    id: 'Pricing.tooltipCopy.KioskModeInactivityTimerTooltipHeading',
    defaultMessage: 'Kiosk Mode: Inactivity Timer',
    desc: {
      id: 'Pricing.tooltipCopy.KioskModeInactivityTimerTooltipHeading',
      message: `${TYPE_HEADER} - 132`,
      project: PROJECT_NAME,
    },
  },
  kiosk_mode_inactivity_timer_tooltip: {
    id: 'Pricing.tooltipCopy.KioskModeInactivityTimerTooltip',
    defaultMessage: `When a survey's inactive for the amount of time you choose, it will loop back to start for the next person. Available in the SurveyMonkey Anywhere app.`,
    desc: {
      id: 'Pricing.tooltipCopy.KioskModeInactivityTimerTooltip',
      message: `${TYPE_PARAGRAPH} - 133`,
      project: PROJECT_NAME,
    },
  },
  kiosk_mode_passcode_lock_tooltip_heading: {
    id: 'Pricing.tooltipCopy.KioskModePasscodeLockTooltipHeading',
    defaultMessage: 'Kiosk Mode: Passcode Lock',
    desc: {
      id: 'Pricing.tooltipCopy.KioskModePasscodeLockTooltipHeading',
      message: `${TYPE_HEADER} - 134`,
      project: PROJECT_NAME,
    },
  },
  kiosk_mode_passcode_lock_tooltip: {
    id: 'Pricing.tooltipCopy.KioskModePasscodeLockTooltip',
    defaultMessage:
      'Keep your device secure-set a passcode that must be entered to exit the survey. Available in the SurveyMonkey Anywhere app.',
    desc: {
      id: 'Pricing.tooltipCopy.KioskModePasscodeLockTooltip',
      message: `${TYPE_PARAGRAPH} - 135`,
      project: PROJECT_NAME,
    },
  },
  labeling_titles_numbering_tooltip_heading: {
    id: 'Pricing.tooltipCopy.LabelingTitlesNumberingTooltipHeading',
    defaultMessage: 'Labeling, titles & numbering',
    desc: {
      id: 'Pricing.tooltipCopy.LabelingTitlesNumberingTooltipHeading',
      message: `${TYPE_HEADER} - 136`,
      project: PROJECT_NAME,
    },
  },
  labeling_titles_numbering_tooltip: {
    id: 'Pricing.tooltipCopy.LabelingTitlesNumberingTooltip',
    defaultMessage:
      'Guide respondents through your surveys with labels, titles, and page numbers for any page in your surveys.',
    desc: {
      id: 'Pricing.tooltipCopy.LabelingTitlesNumberingTooltip',
      message: `${TYPE_PARAGRAPH} - 137`,
      project: PROJECT_NAME,
    },
  },
  menu_matrix_enabled_tooltip_heading: {
    id: 'Pricing.tooltipCopy.MenuMatrixEnabledTooltipHeading',
    defaultMessage: 'Matrix of dropdown menus',
    desc: {
      id: 'Pricing.tooltipCopy.MenuMatrixEnabledTooltipHeading',
      message: `${TYPE_HEADER} - 138`,
      project: PROJECT_NAME,
    },
  },
  menu_matrix_enabled_tooltip: {
    id: 'Pricing.tooltipCopy.MenuMatrixEnabledTooltip',
    defaultMessage:
      'Allow respondents to evaluate several items using the same set of measurements by choosing from a preset list of answer choices.',
    desc: {
      id: 'Pricing.tooltipCopy.MenuMatrixEnabledTooltip',
      message: `${TYPE_PARAGRAPH} - 139`,
      project: PROJECT_NAME,
    },
  },
  mobile_apps_ios_android_tooltip_heading: {
    id: 'Pricing.tooltipCopy.MobileIosAndroidAppsTooltipHeading',
    defaultMessage: 'Mobile apps for iOS and Android',
    desc: {
      id: 'Pricing.tooltipCopy.MobileIosAndroidAppsTooltipHeading',
      message: `${TYPE_HEADER} - 140`,
      project: PROJECT_NAME,
    },
  },
  mobile_apps_ios_android_tooltip: {
    id: 'Pricing.tooltipCopy.MobileIosAndroidAppsTooltip',
    defaultMessage:
      'Access the essential features you need to create a survey, collect responses, and analyze results with the free SurveyMonkey app for iOS and Android.',
    desc: {
      id: 'Pricing.tooltipCopy.MobileIosAndroidAppsTooltip',
      message: `${TYPE_PARAGRAPH} - 141`,
      project: PROJECT_NAME,
    },
  },
  mobile_sdk_pull_response_data_enabled_tooltip_heading: {
    id: 'Pricing.tooltipCopy.MobileSdkPullResponseDataEnabledTooltipHeading',
    defaultMessage: 'Mobile SDK - Data Pull',
    desc: {
      id: 'Pricing.tooltipCopy.MobileSdkPullResponseDataEnabledTooltipHeading',
      message: `${TYPE_HEADER} - 142`,
      project: PROJECT_NAME,
    },
  },
  mobile_sdk_pull_response_data_enabled_tooltip: {
    id: 'Pricing.tooltipCopy.MobileSdkPullResponseDataEnabledTooltip',
    defaultMessage:
      'Take actions in your app based on survey responses collected through the Mobile SDK. For example, prompt people to leave a review if they enter a positive rating.',
    desc: {
      id: 'Pricing.tooltipCopy.MobileSdkPullResponseDataEnabledTooltip',
      message: `${TYPE_PARAGRAPH} - 143`,
      project: PROJECT_NAME,
    },
  },
  multilingual_tooltip_heading: {
    id: 'Pricing.tooltipCopy.MultilingualTooltipHeading',
    defaultMessage: 'Multilingual surveys',
    desc: {
      id: 'Pricing.tooltipCopy.MultilingualTooltipHeading',
      message: `${TYPE_HEADER} - 144`,
      project: PROJECT_NAME,
    },
  },
  multilingual_tooltip: {
    id: 'Pricing.tooltipCopy.MultilingualTooltip',
    defaultMessage: `Reach everyone in the language they're most comfortable with. Add multiple languages to a single survey, and analyze all your results together as one data set.`,
    desc: {
      id: 'Pricing.tooltipCopy.MultilingualTooltip',
      message: `${TYPE_PARAGRAPH} - 145`,
      project: PROJECT_NAME,
    },
  },
  multiple_block_rotation_enabled_tooltip_heading: {
    id: 'Pricing.tooltipCopy.MultipleBlockRotationEnabledTooltipHeading',
    defaultMessage: 'Block randomization',
    desc: {
      id: 'Pricing.tooltipCopy.MultipleBlockRotationEnabledTooltipHeading',
      message: `${TYPE_HEADER} - 146`,
      project: PROJECT_NAME,
    },
  },
  multiple_block_rotation_enabled_tooltip: {
    id: 'Pricing.tooltipCopy.MultipleBlockRotationEnabledTooltip',
    defaultMessage:
      'Blocks are groups of pages, where each group contains related questions about a particular concept you want to test. You can use block randomization logic to rotate, flip, or randomize blocks to minimize order bias.',
    desc: {
      id: 'Pricing.tooltipCopy.MultipleBlockRotationEnabledTooltip',
      message: `${TYPE_PARAGRAPH} - 147`,
      project: PROJECT_NAME,
    },
  },
  num_free_responses_tooltip_heading: {
    id: 'Pricing.tooltipCopy.NumFreeResponsesTooltipHeading',
    defaultMessage: NUM_RESPONSES,
    desc: {
      id: 'Pricing.tooltipCopy.NumFreeResponsesTooltipHeading',
      message: `${TYPE_HEADER} - 148`,
      project: PROJECT_NAME,
    },
  },
  num_free_responses_tooltip: {
    id: 'Pricing.tooltipCopy.NumFreeResponsesTooltip',
    defaultMessage: NUM_RESPONSES_TOOLTIP,
    desc: {
      id: 'Pricing.tooltipCopy.NumFreeResponsesTooltip',
      message: `${TYPE_PARAGRAPH} - 149`,
      project: PROJECT_NAME,
    },
  },
  payment_qt_tooltip_heading: {
    id: 'Pricing.tooltipCopy.PaymentQTTooltipHeading',
    defaultMessage: 'Accept payments',
    desc: {
      id: 'Pricing.tooltipCopy.PaymentQTTooltipHeading',
      message: `${TYPE_HEADER} - 150`,
      project: PROJECT_NAME,
    },
  },
  payment_qt_tooltip: {
    id: 'Pricing.tooltipCopy.PaymentQTTooltip',
    defaultMessage: 'Accept credit card payments from respondents in your surveys with our Stripe integration.',
    desc: {
      id: 'Pricing.tooltipCopy.PaymentQTTooltip',
      message: `${TYPE_PARAGRAPH} - 151`,
      project: PROJECT_NAME,
    },
  },
  phone_support_tooltip_heading: {
    id: 'Pricing.tooltipCopy.PhoneSupportTooltipHeading',
    defaultMessage: SUPPORT,
    desc: {
      id: 'Pricing.tooltipCopy.PhoneSupportTooltipHeading',
      message: `${TYPE_HEADER} - 152`,
      project: PROJECT_NAME,
    },
  },
  phone_support_tooltip: {
    id: 'Pricing.tooltipCopy.PhoneSupportTooltip',
    defaultMessage: SUPPORT_TOOLTIP,
    desc: {
      id: 'Pricing.tooltipCopy.PhoneSupportTooltip',
      message: `${TYPE_PARAGRAPH} - 153`,
      project: PROJECT_NAME,
    },
  },
  benchmark_logic_tooltip_heading: {
    id: 'Pricing.tooltipCopy.PlansBenchmarkTooltipHeading',
    defaultMessage: 'SurveyMonkey industry benchmarks',
    desc: {
      id: 'Pricing.tooltipCopy.PlansBenchmarkTooltipHeading',
      message: `${TYPE_HEADER} - 154`,
      project: PROJECT_NAME,
    },
  },
  benchmark_logic_tooltip: {
    id: 'Pricing.tooltipCopy.PlansBenchmarkTooltip',
    defaultMessage: `Compare your results against the Industry Benchmark to understand how you're doing in the context of your greater industry. See your results in context to see how you measure up against your peers.`,
    desc: {
      id: 'Pricing.tooltipCopy.PlansBenchmarkTooltip',
      message: `${TYPE_PARAGRAPH} - 156`,
      project: PROJECT_NAME,
    },
  },
  plans_benchmarks_tooltip_heading: {
    id: 'Pricing.tooltipCopy.PlansBenchmarksTooltipHeading',
    defaultMessage: 'SurveyMonkey Global Benchmarks',
    desc: {
      id: 'Pricing.tooltipCopy.PlansBenchmarksTooltipHeading',
      message: `${TYPE_HEADER} - 157`,
      project: PROJECT_NAME,
    },
  },
  plans_benchmarks_tooltip: {
    id: 'Pricing.tooltipCopy.PlansBenchmarksTooltip',
    defaultMessage:
      'SurveyMonkey Global Benchmarks give your results context by letting you compare them with the global average. More specific comparison data is available for an extra fee.',
    desc: {
      id: 'Pricing.tooltipCopy.PlansBenchmarksTooltip',
      message: `${TYPE_PARAGRAPH} - 158`,
      project: PROJECT_NAME,
    },
  },
  premium_themes_tooltip_heading: {
    id: 'Pricing.tooltipCopy.PremiumThemesTooltipHeading',
    defaultMessage: 'Premium themes',
    desc: {
      id: 'Pricing.tooltipCopy.PremiumThemesTooltipHeading',
      message: `${TYPE_HEADER} - 159`,
      project: PROJECT_NAME,
    },
  },
  premium_themes_tooltip: {
    id: 'Pricing.tooltipCopy.PremiumThemesTooltip',
    defaultMessage:
      'Select from pre-designed themes to pick a survey theme that matches the topic of your survey. You can also edit the premium themes to create a fully customized design using colors, fonts, images and layouts.',
    desc: {
      id: 'Pricing.tooltipCopy.PremiumThemesTooltip',
      message: `${TYPE_PARAGRAPH} - 160`,
      project: PROJECT_NAME,
    },
  },
  priority_email_support_tooltip_heading: {
    id: 'Pricing.tooltipCopy.PrimaryEmailSupportTooltipHeading',
    defaultMessage: SUPPORT,
    desc: {
      id: 'Pricing.tooltipCopy.PrimaryEmailSupportTooltipHeading',
      message: `${TYPE_HEADER} - 161`,
      project: PROJECT_NAME,
    },
  },
  priority_email_support_tooltip: {
    id: 'Pricing.tooltipCopy.PrimaryEmailSupportTooltip',
    defaultMessage: SUPPORT_TOOLTIP,
    desc: {
      id: 'Pricing.tooltipCopy.PrimaryEmailSupportTooltip',
      message: `${TYPE_PARAGRAPH} - 162`,
      project: PROJECT_NAME,
    },
  },
  private_apps_tooltip_heading: {
    id: 'Pricing.tooltipCopy.PrivateAppsTooltipHeading',
    defaultMessage: 'Unlimited API access',
    desc: {
      id: 'Pricing.tooltipCopy.PrivateAppsTooltipHeading',
      message: `${TYPE_HEADER} - 163`,
      project: PROJECT_NAME,
    },
  },
  private_apps_tooltip: {
    id: 'Pricing.tooltipCopy.PrivateAppsTooltip',
    defaultMessage:
      'Get unlimited access to the SurveyMonkey API to integrate survey data into your mobile and web applications.',
    desc: {
      id: 'Pricing.tooltipCopy.PrivateAppsTooltip',
      message: `${TYPE_PARAGRAPH} - 164`,
      project: PROJECT_NAME,
    },
  },
  question_library_tooltip_heading: {
    id: 'Pricing.tooltipCopy.QuestionLibraryTooltipHeading',
    defaultMessage: 'Question Bank approved questions',
    desc: {
      id: 'Pricing.tooltipCopy.QuestionLibraryTooltipHeading',
      message: `${TYPE_HEADER} - 165`,
      project: PROJECT_NAME,
    },
  },
  question_library_tooltip: {
    id: 'Pricing.tooltipCopy.QuestionLibraryTooltip',
    defaultMessage:
      'Rely on over 1800 pre-written survey questions (and 180+ survey templates), written by experts to make it easier to get great results.',
    desc: {
      id: 'Pricing.tooltipCopy.QuestionLibraryTooltip',
      message: `${TYPE_PARAGRAPH} - 166`,
      project: PROJECT_NAME,
    },
  },
  quizzes_pro_tooltip_heading: {
    id: 'Pricing.tooltipCopy.QuizzesProTooltipHeading',
    defaultMessage: 'Quizzes',
    desc: {
      id: 'Pricing.tooltipCopy.QuizzesProTooltipHeading',
      message: `${TYPE_HEADER} - 167`,
      project: PROJECT_NAME,
    },
  },
  quizzes_pro_tooltip: {
    id: 'Pricing.tooltipCopy.QuizzesProTooltip',
    defaultMessage: `Easily create tests and assessments with automatic scoring for employees, customers, students, and more. You can choose whether you want to display your respondent's score to them after they complete your quiz. You'll also be able to see rankings, segment response data, and access statistics for your results.`,
    desc: {
      id: 'Pricing.tooltipCopy.QuizzesProTooltip',
      message: `${TYPE_PARAGRAPH} - 168`,
      project: PROJECT_NAME,
    },
  },
  randomize_answer_choices_tooltip_heading: {
    id: 'Pricing.tooltipCopy.RandomizeAnswerChoicesTooltipHeading',
    defaultMessage: 'Randomize Answer Choices',
    desc: {
      id: 'Pricing.tooltipCopy.RandomizeAnswerChoicesTooltipHeading',
      message: `${TYPE_HEADER} - 169`,
      project: PROJECT_NAME,
    },
  },
  randomize_answer_choices_tooltip: {
    id: 'Pricing.tooltipCopy.RandomizeAnswerChoicesTooltip',
    defaultMessage: 'Reduce bias by randomly changing the order of answer choices for different respondents.',
    desc: {
      id: 'Pricing.tooltipCopy.RandomizeAnswerChoicesTooltip',
      message: `${TYPE_PARAGRAPH} - 170`,
      project: PROJECT_NAME,
    },
  },
  record_respondent_email_address_tooltip_heading: {
    id: 'Pricing.tooltipCopy.RecordRespondentEmailAddressTooltipHeading',
    defaultMessage: 'Record respondent email address',
    desc: {
      id: 'Pricing.tooltipCopy.RecordRespondentEmailAddressTooltipHeading',
      message: `${TYPE_HEADER} - 171`,
      project: PROJECT_NAME,
    },
  },
  record_respondent_email_address_tooltip: {
    id: 'Pricing.tooltipCopy.RecordRespondentEmailAddressTooltip',
    defaultMessage:
      'Automatically record the email address of respondents to track who takes your surveys, or to follow up with them later.',
    desc: {
      id: 'Pricing.tooltipCopy.RecordRespondentEmailAddressTooltip',
      message: `${TYPE_PARAGRAPH} - 172`,
      project: PROJECT_NAME,
    },
  },
  recurring_surveys_tooltip_heading: {
    id: 'Pricing.tooltipCopy.RecurringSurveysTooltipHeading',
    defaultMessage: 'Recurring surveys',
    desc: {
      id: 'Pricing.tooltipCopy.RecurringSurveysTooltipHeading',
      message: `${TYPE_HEADER} - 173`,
      project: PROJECT_NAME,
    },
  },
  recurring_surveys_tooltip: {
    id: 'Pricing.tooltipCopy.RecurringSurveysTooltip',
    defaultMessage: `Recurring surveys make it easy to collect new results for the same survey on a weekly, monthly, or quarterly basis. We'll email you new survey links when they're ready, so all you need to do is copy each new link and send it to respondents. Filter your results by each occurrence to see how people responded differently over time.`,
    desc: {
      id: 'Pricing.tooltipCopy.RecurringSurveysTooltip',
      message: `${TYPE_PARAGRAPH} - 174`,
      project: PROJECT_NAME,
    },
  },
  respondent_receipt_tooltip_heading: {
    id: 'Pricing.tooltipCopy.RespondentReceiptTooltipHeading',
    defaultMessage: 'Confirmation Email to Respondents',
    desc: {
      id: 'Pricing.tooltipCopy.RespondentReceiptTooltipHeading',
      message: `${TYPE_HEADER} - 175`,
      project: PROJECT_NAME,
    },
  },
  respondent_receipt_tooltip: {
    id: 'Pricing.tooltipCopy.RespondentReceiptTooltip',
    defaultMessage:
      'When distributing surveys with the email collector, send a confirmation email to each respondent with a copy of their answers.',
    desc: {
      id: 'Pricing.tooltipCopy.RespondentReceiptTooltip',
      message: `${TYPE_PARAGRAPH} - 176`,
      project: PROJECT_NAME,
    },
  },
  response_alerts_tooltip_heading: {
    id: 'Pricing.tooltipCopy.ResponseAlertsTooltipHeading',
    defaultMessage: 'Response alerts',
    desc: {
      id: 'Pricing.tooltipCopy.ResponseAlertsTooltipHeading',
      message: `${TYPE_HEADER} - 177`,
      project: PROJECT_NAME,
    },
  },
  response_alerts_tooltip: {
    id: 'Pricing.tooltipCopy.ResponseAlertsTooltip',
    defaultMessage: `Receive email notifications about new survey responses. You can share Response Alerts with other people (in or outside your team)-as long as you're the survey owner.`,
    desc: {
      id: 'Pricing.tooltipCopy.ResponseAlertsTooltip',
      message: `${TYPE_PARAGRAPH} - 178`,
      project: PROJECT_NAME,
    },
  },
  response_quality_enabled_tooltip_heading: {
    id: 'Pricing.tooltipCopy.ResponseQualityEnabledTooltipHeading',
    defaultMessage: 'Response quality',
    desc: {
      id: 'Pricing.tooltipCopy.ResponseQualityEnabledTooltipHeading',
      message: `${TYPE_HEADER} - 179`,
      project: PROJECT_NAME,
    },
  },
  response_quality_enabled_tooltip: {
    id: 'Pricing.tooltipCopy.ResponseQualityEnabledTooltip',
    defaultMessage:
      'Response Quality uses machine learning to scan your survey results and flag poor-quality responses.',
    desc: {
      id: 'Pricing.tooltipCopy.ResponseQualityEnabledTooltip',
      message: `${TYPE_PARAGRAPH} - 180`,
      project: PROJECT_NAME,
    },
  },
  send_surveys_tooltip_heading: {
    id: 'Pricing.tooltipCopy.SendSurveysTooltipHeading',
    defaultMessage: 'Send surveys',
    desc: {
      id: 'Pricing.tooltipCopy.SendSurveysTooltipHeading',
      message: `${TYPE_HEADER} - 181`,
      project: PROJECT_NAME,
    },
  },
  send_surveys_tooltip: {
    id: 'Pricing.tooltipCopy.SendSurveysTooltip',
    defaultMessage: 'Seamlessly send surveys using powerful 3rd-party integration tools.',
    desc: {
      id: 'Pricing.tooltipCopy.SendSurveysTooltip',
      message: `${TYPE_PARAGRAPH} - 182`,
      project: PROJECT_NAME,
    },
  },
  share_surveys_tooltip_heading: {
    id: 'Pricing.tooltipCopy.ShareSurveysTooltipHeading',
    defaultMessage: 'Share survey data',
    desc: {
      id: 'Pricing.tooltipCopy.ShareSurveysTooltipHeading',
      message: `${TYPE_HEADER} - 183`,
      project: PROJECT_NAME,
    },
  },
  share_surveys_tooltip: {
    id: 'Pricing.tooltipCopy.ShareSurveysTooltip',
    defaultMessage: 'Invite anyone to view data from your survey.',
    desc: {
      id: 'Pricing.tooltipCopy.ShareSurveysTooltip',
      message: `${TYPE_PARAGRAPH} - 184`,
      project: PROJECT_NAME,
    },
  },
  shared_assets_tooltip_heading: {
    id: 'Pricing.tooltipCopy.SharedGroupAssetLibraryTooltipHeading',
    defaultMessage: 'Shared library',
    desc: {
      id: 'Pricing.tooltipCopy.SharedGroupAssetLibraryTooltipHeading',
      message: `${TYPE_HEADER} - 185`,
      project: PROJECT_NAME,
    },
  },
  shared_assets_tooltip: {
    id: 'Pricing.tooltipCopy.SharedGroupAssetsLibraryTooltip',
    defaultMessage: 'Get access to shared images, documents, themes, and templates that everyone in your team can use.',
    desc: {
      id: 'Pricing.tooltipCopy.SharedGroupAssetsLibraryTooltip',
      message: `${TYPE_PARAGRAPH} - 186`,
      project: PROJECT_NAME,
    },
  },
  shared_library_tooltip_heading: {
    id: 'Pricing.tooltipCopy.SharedLibraryTooltipHeading',
    defaultMessage: 'Shared library',
    desc: {
      id: 'Pricing.tooltipCopy.SharedLibraryTooltipHeading',
      message: `${TYPE_HEADER} - 187`,
      project: PROJECT_NAME,
    },
  },
  shared_library_tooltip: {
    id: 'Pricing.tooltipCopy.SharedLibraryTooltip',
    defaultMessage: 'Get access to shared images, documents, themes, and templates that everyone in your team can use.',
    desc: {
      id: 'Pricing.tooltipCopy.SharedLibraryTooltip',
      message: `${TYPE_PARAGRAPH} - 188`,
      project: PROJECT_NAME,
    },
  },
  show_create_crosstab_tooltip_heading: {
    id: 'Pricing.tooltipCopy.ShowCreateCrosstabTooltipHeading',
    defaultMessage: 'Crosstabs',
    desc: {
      id: 'Pricing.tooltipCopy.ShowCreateCrosstabTooltipHeading',
      message: `${TYPE_HEADER} - 189`,
      project: PROJECT_NAME,
    },
  },
  show_create_crosstab_tooltip: {
    id: 'Pricing.tooltipCopy.ShowCreateCrosstabTooltip',
    defaultMessage:
      'Run advanced comparisons across multiple questions, uncover statistically significant differences, and share your crosstab report with anyone.',
    desc: {
      id: 'Pricing.tooltipCopy.ShowCreateCrosstabTooltip',
      message: `${TYPE_PARAGRAPH} - 190`,
      project: PROJECT_NAME,
    },
  },
  show_sig_diffs_enabled_tooltip_heading: {
    id: 'Pricing.tooltipCopy.ShowSigDiffsEnabledTooltipHeading',
    defaultMessage: 'Statistical significance',
    desc: {
      id: 'Pricing.tooltipCopy.ShowSigDiffsEnabledTooltipHeading',
      message: `${TYPE_HEADER} - 191`,
      project: PROJECT_NAME,
    },
  },
  show_sig_diffs_enabled_tooltip: {
    id: 'Pricing.tooltipCopy.ShowSigDiffsEnabledTooltip',
    defaultMessage: 'Use to see if there are statistically significant differences between response groups.',
    desc: {
      id: 'Pricing.tooltipCopy.ShowSigDiffsEnabledTooltip',
      message: `${TYPE_PARAGRAPH} - 192`,
      project: PROJECT_NAME,
    },
  },
  single_sign_on_tooltip_heading: {
    id: 'Pricing.tooltipCopy.SingleSignOnTooltipHeading',
    defaultMessage: 'Single sign-on',
    desc: {
      id: 'Pricing.tooltipCopy.SingleSignOnTooltipHeading',
      message: `${TYPE_HEADER} - 193`,
      project: PROJECT_NAME,
    },
  },
  single_sign_on_tooltip: {
    id: 'Pricing.tooltipCopy.SingleSignOnTooltip',
    defaultMessage:
      'Control access to SurveyMonkey across the organization and define authentication policies for increased security and governance.',
    desc: {
      id: 'Pricing.tooltipCopy.SingleSignOnTooltip',
      message: `${TYPE_PARAGRAPH} - 194`,
      project: PROJECT_NAME,
    },
  },
  skip_logic_tooltip_heading: {
    id: 'Pricing.tooltipCopy.CreateSkipLogicEnabledHeading',
    defaultMessage: 'Customized survey experience',
    desc: {
      id: 'Pricing.tooltipCopy.CreateSkipLogicEnabledHeading',
      message: `${TYPE_HEADER} - 195`,
      project: PROJECT_NAME,
    },
  },
  skip_logic_tooltip: {
    id: 'Pricing.tooltipCopy.SkipLogicEnabledTooltip',
    defaultMessage: `Get more ways to personalize each respondent's experience while taking your survey. Use skip logic to create custom paths through a survey based on each respondent's answers. With question & answer piping, you can insert answer text from a question into another question on a later page in the survey.`,
    desc: {
      id: 'Pricing.tooltipCopy.SkipLogicEnabledTooltip',
      message: `${TYPE_PARAGRAPH} - 196`,
      project: PROJECT_NAME,
    },
  },
  statistical_significance_tooltip_heading: {
    id: 'Pricing.tooltipCopy.StatisticalSignificanceTooltipHeading',
    defaultMessage: ADVANCED_SURVEY_LOGIC,
    desc: {
      id: 'Pricing.tooltipCopy.StatisticalSignificanceTooltipHeading',
      message: `${TYPE_HEADER} - 197`,
      project: PROJECT_NAME,
    },
  },
  statistical_significance_tooltip: {
    id: 'Pricing.tooltipCopy.StatisticalSignificanceTooltip',
    defaultMessage: ADVANCED_SURVEY_LOGIC_TOOLTIP,
    desc: {
      id: 'Pricing.tooltipCopy.StatisticalSignificanceTooltip',
      message: `${TYPE_PARAGRAPH} - 198`,
      project: PROJECT_NAME,
    },
  },
  survey_branding_tooltip_heading: {
    id: 'Pricing.tooltipCopy.SurveyBrandingTooltipHeading',
    defaultMessage: 'Customized surveys',
    desc: {
      id: 'Pricing.tooltipCopy.SurveyBrandingTooltipHeading',
      message: `${TYPE_HEADER} - 199`,
      project: PROJECT_NAME,
    },
  },
  survey_branding_tooltip: {
    id: 'Pricing.tooltipCopy.SurveyBrandingTooltip',
    defaultMessage: `Customize your survey's look and feel by adding your logo, selecting colors and web fonts, and uploading images. Create and save custom themes to quickly build on-brand surveys. You can also modify the ending of your survey's URL. For example: www.surveymonkey.com/s/your-survey`,
    desc: {
      id: 'Pricing.tooltipCopy.SurveyBrandingTooltip',
      message: `${TYPE_PARAGRAPH} - 200`,
      project: PROJECT_NAME,
    },
  },
  survey_create_limit_tooltip_heading: {
    id: 'Pricing.tooltipCopy.SurveyCreateLimitTooltipHeading',
    defaultMessage: 'Number of surveys',
    desc: {
      id: 'Pricing.tooltipCopy.SurveyCreateLimitTooltipHeading',
      message: `${TYPE_HEADER} - 201`,
      project: PROJECT_NAME,
    },
  },
  survey_create_limit_tooltip: {
    id: 'Pricing.tooltipCopy.SurveyCreateLimitTooltip',
    defaultMessage: 'You can create and send as many surveys, polls, and quizzes as you need with any plan.',
    desc: {
      id: 'Pricing.tooltipCopy.SurveyCreateLimitTooltip',
      message: `${TYPE_PARAGRAPH} - 202`,
      project: PROJECT_NAME,
    },
  },
  team_activity_tooltip_heading: {
    id: 'Pricing.tooltipCopy.TeamActivityTooltipHeading',
    defaultMessage: 'Team activity',
    desc: {
      id: 'Pricing.tooltipCopy.TeamActivityTooltipHeading',
      message: `${TYPE_HEADER} - 203`,
      project: PROJECT_NAME,
    },
  },
  team_activity_tooltip: {
    id: 'Pricing.tooltipCopy.TeamActivityTooltip',
    defaultMessage: `Track all user activity in your Enterprise account, such as user logins, survey creation, survey sharing, survey exports, etc. with the ability to search and filter through audit logs.`,
    desc: {
      id: 'Pricing.tooltipCopy.TeamActivityTooltip',
      message: `${TYPE_PARAGRAPH} - 204`,
      project: PROJECT_NAME,
    },
  },
  team_logic_tooltip_heading: {
    id: 'Pricing.tooltipCopy.TeamLogicTooltipHeading',
    defaultMessage: 'Team management',
    desc: {
      id: 'Pricing.tooltipCopy.TeamLogicTooltipHeading',
      message: `${TYPE_HEADER} - 205`,
      project: PROJECT_NAME,
    },
  },
  team_logic_tooltip: {
    id: 'Pricing.tooltipCopy.TeamLogicTooltip',
    defaultMessage: `If you're the Primary Admin or Admin of a team, you can invite more people to join the team and manage existing accounts.`,
    desc: {
      id: 'Pricing.tooltipCopy.TeamLogicTooltip',
      message: `${TYPE_PARAGRAPH} - 206`,
      project: PROJECT_NAME,
    },
  },
  team_ownership_tooltip_heading: {
    id: 'Pricing.tooltipCopy.TeamOwnershipTooltipHeading',
    defaultMessage: 'Team ownership',
    desc: {
      id: 'Pricing.tooltipCopy.TeamOwnershipTooltipHeading',
      message: `${TYPE_HEADER} - 207`,
      project: PROJECT_NAME,
    },
  },
  team_ownership_tooltip: {
    id: 'Pricing.tooltipCopy.TeamOwnershipTooltip',
    defaultMessage: 'Choose whether you or your organization owns all the accounts within your team in SurveyMonkey.',
    desc: {
      id: 'Pricing.tooltipCopy.TeamOwnershipTooltip',
      message: `${TYPE_PARAGRAPH} - 208`,
      project: PROJECT_NAME,
    },
  },
  team_themes_tooltip_heading: {
    id: 'Pricing.tooltipCopy.TeamThemesTooltipHeading',
    defaultMessage: 'Shared themes',
    desc: {
      id: 'Pricing.tooltipCopy.TeamThemesTooltipHeading',
      message: `${TYPE_HEADER} - 209`,
      project: PROJECT_NAME,
    },
  },
  team_themes_tooltip: {
    id: 'Pricing.tooltipCopy.TeamThemesTooltip',
    defaultMessage: `Share custom themes with your team to stay consistent with your brand's look and feel.`,
    desc: {
      id: 'Pricing.tooltipCopy.TeamThemesTooltip',
      message: `${TYPE_PARAGRAPH} - 210`,
      project: PROJECT_NAME,
    },
  },
  text_analysis_tooltip_heading: {
    id: 'Pricing.tooltipCopy.TextAnalysisTooltipHeading',
    defaultMessage: ADVANCED_SURVEY_LOGIC,
    desc: {
      id: 'Pricing.tooltipCopy.TextAnalysisTooltipHeading',
      message: `${TYPE_HEADER} - 211`,
      project: PROJECT_NAME,
    },
  },
  text_analysis_tooltip: {
    id: 'Pricing.tooltipCopy.TextAnalysisTooltip',
    defaultMessage: ADVANCED_SURVEY_LOGIC_TOOLTIP,
    desc: {
      id: 'Pricing.tooltipCopy.TextAnalysisTooltip',
      message: `${TYPE_PARAGRAPH} - 212`,
      project: PROJECT_NAME,
    },
  },
  unlimited_questions_tooltip_heading: {
    id: 'Pricing.tooltipCopy.UnlimitedQuestionsTooltipHeading',
    defaultMessage: 'Questions per survey',
    desc: {
      id: 'Pricing.tooltipCopy.UnlimitedQuestionsTooltipHeading',
      message: `${TYPE_HEADER} - 213`,
      project: PROJECT_NAME,
    },
  },
  unlimited_questions_tooltip: {
    id: 'Pricing.tooltipCopy.UnlimitedQuestionsTooltip',
    defaultMessage:
      'Some of our plans only allow a certain number of questions for each survey you send. All plans allow you to create and send as many surveys as you want.',
    desc: {
      id: 'Pricing.tooltipCopy.UnlimitedQuestionsTooltip',
      message: `${TYPE_PARAGRAPH} - 214`,
      project: PROJECT_NAME,
    },
  },
  unlimited_responses_tooltip_heading: {
    id: 'Pricing.tooltipCopy.UnlimitedResponsesTooltipHeading',
    defaultMessage: NUM_RESPONSES,
    desc: {
      id: 'Pricing.tooltipCopy.UnlimitedResponsesTooltipHeading',
      message: `${TYPE_HEADER} - 215`,
      project: PROJECT_NAME,
    },
  },
  unlimited_responses_tooltip: {
    id: 'Pricing.tooltipCopy.UnlimitedResponsesTooltip',
    defaultMessage: NUM_RESPONSES_TOOLTIP,
    desc: {
      id: 'Pricing.tooltipCopy.UnlimitedResponsesTooltip',
      message: `${TYPE_PARAGRAPH} - 216`,
      project: PROJECT_NAME,
    },
  },
  user_management_tooltip_heading: {
    id: 'Pricing.tooltipCopy.UserManagementTooltipHeading',
    defaultMessage: 'Additional users',
    desc: {
      id: 'Pricing.tooltipCopy.UserManagementTooltipHeading',
      message: `${TYPE_HEADER} - 217`,
      project: PROJECT_NAME,
    },
  },
  user_management_tooltip: {
    id: 'Pricing.tooltipCopy.UserManagementTooltip',
    defaultMessage:
      'Business plans start at 3 Full Users, and allow you to invite more Full Users to your SurveyMonkey team for an additional fee. With a team plan, you can add Contributor seats in bundles of 2 as a cost-effective way to add stakeholders to focus solely on analyzing survey results. Enterprise plans allow you to add as many users as you need across your organization and allows for broad collaboration at scale.',
    desc: {
      id: 'Pricing.tooltipCopy.UserManagementTooltip',
      message: `${TYPE_PARAGRAPH} - 218`,
      project: PROJECT_NAME,
    },
  },
  validate_answer_tooltip_heading: {
    id: 'Pricing.tooltipCopy.ValidateAnswerTooltipHeading',
    defaultMessage: 'Answer validation',
    desc: {
      id: 'Pricing.tooltipCopy.ValidateAnswerTooltipHeading',
      message: `${TYPE_HEADER} - 219`,
      project: PROJECT_NAME,
    },
  },
  validate_answer_tooltip: {
    id: 'Pricing.tooltipCopy.ValidateAnswerTooltip',
    defaultMessage: 'Require the text in survey responses to meet formatting and content criteria you set.',
    desc: {
      id: 'Pricing.tooltipCopy.ValidateAnswerTooltip',
      message: `${TYPE_PARAGRAPH} - 220`,
      project: PROJECT_NAME,
    },
  },
  view_respondent_ip_address_tooltip_heading: {
    id: 'Pricing.tooltipCopy.ViewRespondentIpAddressTooltipHeading',
    defaultMessage: `View respondent's IP address`,
    desc: {
      id: 'Pricing.tooltipCopy.ViewRespondentIpAddressTooltipHeading',
      message: `${TYPE_HEADER} - 221`,
      project: PROJECT_NAME,
    },
  },
  view_respondent_ip_address_tooltip: {
    id: 'Pricing.tooltipCopy.ViewRespondentIpAddressTooltip',
    defaultMessage:
      'Automatically record the IP address of respondents to get a better sense of who answers your surveys.',
    desc: {
      id: 'Pricing.tooltipCopy.ViewRespondentIpAddressTooltip',
      message: `${TYPE_PARAGRAPH} - 222`,
      project: PROJECT_NAME,
    },
  },
  word_cloud_combine_hide_tooltip_heading: {
    id: 'Pricing.tooltipCopy.WordCloudCombineHideTooltipHeading',
    defaultMessage: 'Word clouds: Combine or hide words',
    desc: {
      id: 'Pricing.tooltipCopy.WordCloudCombineHideTooltipHeading',
      message: `${TYPE_HEADER} - 223`,
      project: PROJECT_NAME,
    },
  },
  word_cloud_combine_hide_tooltip: {
    id: 'Pricing.tooltipCopy.WordCloudCombineHideTooltip',
    defaultMessage:
      'Get more control over the words that appear in your word cloud. Combine similar words, hide others, and choose the frequency of words to include.',
    desc: {
      id: 'Pricing.tooltipCopy.WordCloudCombineHideTooltip',
      message: `${TYPE_PARAGRAPH} - 224`,
      project: PROJECT_NAME,
    },
  },
});
